import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import AdminLayout from '../../../components/AdminLayout';
import ContentBox from '../../../components/ContentBox';
import HabboImage from '../../../components/HabboImage';
import { searchLookupUsers, getGdprErasurePreview, eraseUserForGdpr } from '../../../services/api';

const EXACT_PIXEL_IMAGE = {
    width: 'auto',
    height: 'auto',
    maxWidth: 'none',
    maxHeight: 'none',
    objectFit: 'none',
    flexShrink: 0,
};

const inputClass = 'w-full px-3 py-2 border border-gray-300 rounded text-sm';
const smallButtonShadow = { boxShadow: '0 2px 0 0 rgba(0, 0, 0, 0.3)' };

const formatNumber = (value) => Number(value || 0).toLocaleString('en-GB');

const formatDate = (value) => {
    if (!value) return '-';
    return new Date(value).toLocaleDateString('en-GB', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
    });
};

const groupItems = (items) => items.reduce((groups, item) => {
    (groups[item.group] = groups[item.group] || []).push(item);
    return groups;
}, {});

function GdprDelete({ userData }) {
    const [term, setTerm] = useState('');
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [confirmName, setConfirmName] = useState('');
    const [erasing, setErasing] = useState(false);
    const [erased, setErased] = useState(null);

    const searchToken = useRef(0);

    useEffect(() => {
        const trimmed = term.trim();

        if (trimmed.length < 2) {
            setResults([]);
            setSearching(false);
            return;
        }

        setSearching(true);
        const token = ++searchToken.current;

        const timer = setTimeout(async () => {
            try {
                const response = await searchLookupUsers(trimmed);
                if (token !== searchToken.current) return;
                setResults(response.success ? response.users : []);
            } catch (error) {
                if (token !== searchToken.current) return;
                console.error('Failed to search users:', error);
                setResults([]);
            } finally {
                if (token === searchToken.current) setSearching(false);
            }
        }, 300);

        return () => clearTimeout(timer);
    }, [term]);

    const loadPreview = useCallback(async (userId) => {
        setLoading(true);
        setErased(null);
        setConfirmName('');
        try {
            const response = await getGdprErasurePreview(userId);
            if (response.success) {
                setPreview(response);
                setResults([]);
            } else {
                setPreview(null);
                toast.error(response.message || 'Failed to load this user');
            }
        } catch (error) {
            console.error('Failed to load GDPR preview:', error);
            toast.error('Failed to load this user');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSubmit = (e) => {
        e.preventDefault();
        if (results[0]) loadPreview(results[0].userId);
    };

    const handleErase = async () => {
        if (!preview || erasing) return;
        setErasing(true);
        try {
            const response = await eraseUserForGdpr(preview.user.userId, confirmName.trim());
            if (response.success) {
                toast.success(`${response.habboName} has been erased`);
                setErased(response);
                setPreview(null);
                setConfirmName('');
                setTerm('');
            } else {
                toast.error(response.message || 'The erasure failed');
            }
        } catch (error) {
            console.error('GDPR erasure failed:', error);
            toast.error('The erasure failed - nothing was deleted');
        } finally {
            setErasing(false);
        }
    };

    if (!userData || !userData.hasAdminRole) {
        return <Navigate to="/" replace />;
    }

    const nameMatches = preview
        && confirmName.trim().toLowerCase() === preview.user.habboName.toLowerCase();
    const groups = preview ? groupItems(preview.items.filter((item) => item.count > 0)) : {};

    return (
        <AdminLayout>
            <ContentBox theme="default" tint="red" header="GDPR Delete">
                <p className="text-sm text-gray-600 mb-3">
                    Search for a user to permanently delete their Frankly profile and all data linked to their user ID.
                    This cannot be undone.
                </p>
                <form onSubmit={handleSubmit} className="flex gap-2 mb-3">
                    <input
                        type="text"
                        value={term}
                        onChange={(e) => setTerm(e.target.value)}
                        placeholder="Search by Habbo name..."
                        className={inputClass}
                        autoFocus
                    />
                    <button
                        type="submit"
                        className="bg-maroon text-white px-6 py-2 text-sm uppercase tracking-wider whitespace-nowrap"
                        style={smallButtonShadow}
                    >
                        Find
                    </button>
                </form>

                {searching && <p className="text-sm uppercase text-gray-500">Searching...</p>}

                {!searching && term.trim().length >= 2 && results.length === 0 && (
                    <p className="text-sm uppercase text-gray-500">No Frankly profile found</p>
                )}

                {results.length > 0 && (
                    <div className="space-y-2">
                        {results.map((user) => (
                            <button
                                key={user.userId}
                                onClick={() => loadPreview(user.userId)}
                                className="w-full bg-gray-100 hover:bg-gray-200 p-2 rounded flex items-center gap-3 text-left transition-colors"
                            >
                                <div className="w-10 h-10 border border-black bg-gray-300 overflow-hidden flex items-center justify-center shrink-0">
                                    <HabboImage
                                        habbo={user.habboName}
                                        hotel={user.hotel || 'COM'}
                                        headonly
                                        direction={2}
                                        head_direction={2}
                                        size="s"
                                        style={EXACT_PIXEL_IMAGE}
                                        alt={user.habboName}
                                    />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-bold">{user.habboName}</div>
                                    <div className="text-xs text-gray-600">
                                        {user.hotel || 'COM'} &middot; Joined {formatDate(user.memberSince)}
                                    </div>
                                </div>
                            </button>
                        ))}
                    </div>
                )}
            </ContentBox>

            {loading && (
                <ContentBox theme="default" tint="red" header="Loading">
                    <p className="text-sm uppercase">Gathering this user's data...</p>
                </ContentBox>
            )}

            {!loading && preview && (
                <ContentBox theme="default" tint="red" header={`Erase ${preview.user.habboName}`}>
                    <div className="flex items-start gap-4 mb-4">
                        <div className="w-16 h-20 border border-black bg-gray-300 overflow-hidden flex items-end justify-center shrink-0">
                            <HabboImage
                                habbo={preview.user.habboName}
                                hotel={preview.user.hotel}
                                direction={2}
                                head_direction={2}
                                size="s"
                                style={EXACT_PIXEL_IMAGE}
                                alt={preview.user.habboName}
                            />
                        </div>
                        <div className="flex-1 min-w-0 text-sm space-y-1">
                            <div><span className="uppercase text-gray-500">Hotel:</span> {preview.user.hotel}</div>
                            <div><span className="uppercase text-gray-500">Joined:</span> {formatDate(preview.user.memberSince)}</div>
                            <div className="break-all"><span className="uppercase text-gray-500">User ID:</span> {preview.user.userId}</div>
                            <div>
                                <span className="uppercase text-gray-500">Discord link:</span>{' '}
                                {preview.user.hasDiscordLink ? 'Found (sessions, roles and poll votes included)' : 'Not found'}
                            </div>
                        </div>
                    </div>

                    <div className="text-sm uppercase tracking-wider text-gray-500 mb-2">
                        {formatNumber(preview.total)} records will be deleted
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-4">
                        {Object.entries(groups).map(([group, items]) => (
                            <div key={group} className="bg-gray-100 p-3">
                                <div className="font-bold text-sm mb-1">{group}</div>
                                {items.map((item) => (
                                    <div key={`${item.table}-${item.label}`} className="flex justify-between text-xs text-gray-700">
                                        <span>{item.label}</span>
                                        <span>{formatNumber(item.count)}</span>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>

                    <p className="text-xs text-gray-500 mb-3">
                        Staff-authored site content (articles, events, Observer issues, values edits) is kept.
                        The Discord bot's verification record is not touched by this tool.
                    </p>

                    <label className="block text-sm mb-1">
                        Type <span className="font-bold">{preview.user.habboName}</span> to confirm:
                    </label>
                    <div className="flex gap-2">
                        <input
                            type="text"
                            value={confirmName}
                            onChange={(e) => setConfirmName(e.target.value)}
                            placeholder={preview.user.habboName}
                            className={inputClass}
                        />
                        <button
                            type="button"
                            onClick={handleErase}
                            disabled={!nameMatches || erasing}
                            className="bg-red-700 text-white px-6 py-2 text-sm uppercase tracking-wider whitespace-nowrap disabled:opacity-40 disabled:cursor-not-allowed"
                            style={smallButtonShadow}
                        >
                            {erasing ? 'Deleting...' : 'Delete Everything'}
                        </button>
                    </div>
                </ContentBox>
            )}

            {erased && (
                <ContentBox theme="default" tint="green" header="Erasure Complete">
                    <p className="text-sm mb-2">
                        <span className="font-bold">{erased.habboName}</span> has been erased.{' '}
                        {formatNumber(erased.total)} records and {formatNumber(erased.filesDeleted)} uploaded files were deleted.
                    </p>
                    {erased.deleted.length > 0 && (
                        <div className="text-xs text-gray-700 space-y-0.5">
                            {erased.deleted.map((row) => (
                                <div key={`${row.table}-${row.label}`} className="flex justify-between max-w-md">
                                    <span>{row.label}</span>
                                    <span>{formatNumber(row.count)}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </ContentBox>
            )}
        </AdminLayout>
    );
}

export default GdprDelete;
